import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, FloppyDisk } from "@phosphor-icons/react";

const field = "w-full bg-white/5 border border-white/20 px-3 py-2 text-sm focus:border-primary outline-none transition-colors";

export const fillReply = (body, m) => body
  .replaceAll("{name}", (m.name || "").split(" ")[0] || "there")
  .replaceAll("{order}", m.order_ref || "your order");

const ReplyEditor = ({ initial, onDone, onCancel }) => {
  const [f, setF] = useState({ title: initial?.title || "", body: initial?.body || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title.trim() || !f.body.trim()) { toast.error("Add a title and the reply text"); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/admin/saved-replies/${initial.id}`, f);
      else await api.post("/admin/saved-replies", f);
      toast.success("Saved reply stored"); onDone();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not save reply"); } finally { setSaving(false); }
  };
  return (
    <div data-testid="saved-reply-editor" className="border border-primary/40 p-4 space-y-3">
      <input data-testid="saved-reply-title" placeholder="Title, e.g. Shipping times" maxLength={60} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} />
      <textarea data-testid="saved-reply-body" rows={6} placeholder="Reply text — use {name} and {order} to fill in customer details" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${field} resize-y`} />
      <div className="flex gap-2">
        <button data-testid="saved-reply-save" onClick={save} disabled={saving} className="label-mono text-[0.6rem] px-4 py-2 bg-primary text-black hover:bg-[#E6C766] transition-colors inline-flex items-center gap-1 disabled:opacity-50"><FloppyDisk size={12} /> Save</button>
        <button data-testid="saved-reply-cancel" onClick={onCancel} className="label-mono text-[0.6rem] px-4 py-2 border border-white/20 text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
      </div>
    </div>
  );
};

export const SavedRepliesManager = ({ replies, reload, onClose }) => {
  const [editing, setEditing] = useState(null);
  const remove = async (r) => {
    if (!window.confirm(`Delete saved reply "${r.title}"?`)) return;
    try { await api.delete(`/admin/saved-replies/${r.id}`); toast.success("Saved reply deleted"); reload(); } catch { toast.error("Could not delete reply"); }
  };
  return (
    <div data-testid="saved-replies-manager" className="border border-black/10 bg-surface p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="label-mono text-primary">Saved Replies</span>
        <button data-testid="saved-replies-close" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={14} /></button>
      </div>
      <p className="text-xs text-muted-foreground">Use <code className="text-primary">{"{name}"}</code> for the customer's first name and <code className="text-primary">{"{order}"}</code> for their order #.</p>
      {replies.map((r) => editing === r.id ? (
        <ReplyEditor key={r.id} initial={r} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />
      ) : (
        <div key={r.id} data-testid={`saved-reply-${r.id}`} className="flex items-start justify-between gap-3 border-t border-black/10 pt-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">{r.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-2">{r.body.replace(/^Hi \{name\},?\s*/, "").replace(/\s+/g, " ")}</p>
          </div>
          <div className="flex shrink-0">
            <button data-testid={`saved-reply-edit-${r.id}`} onClick={() => setEditing(r.id)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={14} /></button>
            <button data-testid={`saved-reply-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash size={14} /></button>
          </div>
        </div>
      ))}
      {editing === "new" ? (
        <ReplyEditor onCancel={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />
      ) : (
        <button data-testid="saved-reply-add" onClick={() => setEditing("new")} className="label-mono text-[0.6rem] text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"><Plus size={12} /> New saved reply</button>
      )}
    </div>
  );
};
